import {
  AloftPrice,
  AloftPriceJSON,
  MODEL_RELIABLE_R_SQUARED_MIN,
  MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX,
  RegressionModelDiagnostics,
  RegressionUnitRates,
} from '@aloft/toolkit-shared';
import {
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  Typography,
} from '@aloft/toolkit-sui';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatPrice = (json: AloftPriceJSON): string =>
  AloftPrice.fromUnitAmount(json.unitAmount).toString(true);

const formatPriceRaw = (dollars: number): string =>
  AloftPrice.fromDollarLike(dollars).toString(true);

const formatPct = (n: number): string => `${(n * 100).toFixed(1)}%`;

// ─── Reliable pill ──────────────────────────────────────────────────────────

type ReliablePillProps = { reliable: boolean; reason?: string };

const ReliablePill = ({ reliable, reason }: ReliablePillProps) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 999,
      padding: '2px 10px',
      backgroundColor: reliable ? '#dcfce7' : '#fee2e2',
    }}
  >
    <Typography variant="body2" color={reliable ? 'alertSuccess' : 'alertError'}>
      {reliable ? 'Reliable' : reason ? `Unreliable (${reason})` : 'Unreliable'}
    </Typography>
  </div>
);

const DroppedPill = () => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 999,
      padding: '2px 10px',
      backgroundColor: '#f3f4f6',
    }}
  >
    <Typography variant="body2" color="textSecondary">
      Dropped
    </Typography>
  </div>
);

const ConstraintPill = ({ bound, value }: { bound: 'min' | 'max'; value: number }) => (
  <Tooltip
    label={`Constrained to the ${bound}imum allowed rate: ${formatPriceRaw(value)}`}
    placement="top"
  >
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        padding: '2px 10px',
        backgroundColor: '#fef3c7',
      }}
    >
      <Typography variant="body2" color="textSecondary">
        {bound === 'min' ? 'Min bound' : 'Max bound'}
      </Typography>
    </div>
  </Tooltip>
);

// ─── Stat chip ──────────────────────────────────────────────────────────────

type QualityBand = 'excellent' | 'good' | 'acceptable' | 'weak' | 'poor' | 'neutral';

const QUALITY_BAND_STYLES: Record<QualityBand, { border: string; bg: string; text: string }> = {
  excellent: { border: '#166534', bg: '#dcfce7', text: '#14532d' },
  good: { border: '#4ade80', bg: '#f0fdf4', text: '#166534' },
  acceptable: { border: '#facc15', bg: '#fefce8', text: '#713f12' },
  weak: { border: '#fca5a5', bg: '#fff1f1', text: '#b91c1c' },
  poor: { border: '#991b1b', bg: '#fee2e2', text: '#7f1d1d' },
  neutral: { border: '#e5e7eb', bg: 'transparent', text: '' },
};

const getRSquaredBand = (rSquared: number): QualityBand => {
  if (rSquared >= 0.85) return 'excellent';
  if (rSquared >= 0.7) return 'good';
  if (rSquared >= 0.5) return 'acceptable';
  if (rSquared >= 0.3) return 'weak';
  return 'poor';
};

const getStdErrorRatioBand = (ratio: number): QualityBand => {
  if (ratio < 0.05) return 'excellent';
  if (ratio < 0.08) return 'good';
  if (ratio < 0.12) return 'acceptable';
  if (ratio < 0.15) return 'weak';
  return 'poor';
};

type StatChipProps = { label: string; value: string; band?: QualityBand; tooltip: string };

const StatChip = ({ label, value, band = 'neutral', tooltip }: StatChipProps) => {
  const styles = QUALITY_BAND_STYLES[band];
  return (
    <Tooltip label={tooltip} placement="top">
      <div
        style={{
          borderRadius: '8px',
          border: `1px solid ${styles.border}`,
          backgroundColor: styles.bg,
          padding: '10px 16px',
          minWidth: '120px',
          cursor: 'default',
        }}
      >
        <Flex flexDirection="column" gap="2px">
          <span style={{ color: band === 'neutral' ? undefined : styles.text }}>
            <Typography variant="body2" color={band === 'neutral' ? 'textSecondary' : undefined}>
              {label}
            </Typography>
          </span>
          <span style={{ color: band === 'neutral' ? undefined : styles.text }}>
            <Typography variant="h4">{value}</Typography>
          </span>
        </Flex>
      </div>
    </Tooltip>
  );
};

// ─── Feature config ─────────────────────────────────────────────────────────

type FeatureKey = keyof Required<RegressionUnitRates>;

const FEATURE_ROWS: Array<{ key: FeatureKey; label: string; unit: string }> = [
  { key: 'gla', label: 'GLA', unit: '/sqft' },
  { key: 'bedrooms', label: 'Bedrooms', unit: '/bed' },
  { key: 'fullBaths', label: 'Full Baths', unit: '/bath' },
  { key: 'halfBaths', label: 'Half Baths', unit: '/half bath' },
  { key: 'garageCarport', label: 'Garage/Carport', unit: '/car' },
  { key: 'siteSize', label: 'Site Size', unit: '/sqft' },
];

// ─── Main component ─────────────────────────────────────────────────────────

type RegressionModelOutputProps = {
  constant: AloftPriceJSON;
  unitRates: RegressionUnitRates;
  modelDiagnostics: RegressionModelDiagnostics;
};

export const RegressionModelOutput = ({
  constant,
  unitRates,
  modelDiagnostics,
}: RegressionModelOutputProps) => {
  const unreliableReasons: string[] = [];
  if (!modelDiagnostics.reliable) {
    if (modelDiagnostics.rSquared < MODEL_RELIABLE_R_SQUARED_MIN) {
      unreliableReasons.push(
        `R² ${formatPct(modelDiagnostics.rSquared)} < ${formatPct(MODEL_RELIABLE_R_SQUARED_MIN)}`,
      );
    }
    if (modelDiagnostics.standardErrorRatio > MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX) {
      unreliableReasons.push(
        `Std Error Ratio ${formatPct(modelDiagnostics.standardErrorRatio)} > ${formatPct(MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX)}`,
      );
    }
    if (
      modelDiagnostics.crossValidatedStandardErrorRatio !== undefined &&
      modelDiagnostics.crossValidatedStandardErrorRatio > MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX
    ) {
      unreliableReasons.push(
        `Out-of-sample Error ${formatPct(modelDiagnostics.crossValidatedStandardErrorRatio)} > ${formatPct(MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX)}`,
      );
    }
  }

  return (
    <Flex flexDirection="column" gap="24px">
      {/* Model Diagnostics */}
      <Flex flexDirection="column" gap="12px">
        <Flex alignItems="center" gap="12px">
          <Typography variant="h4">Model Diagnostics</Typography>
          <ReliablePill
            reliable={modelDiagnostics.reliable}
            reason={unreliableReasons.length > 0 ? unreliableReasons.join(', ') : undefined}
          />
        </Flex>
        <Flex flexWrap="wrap" gap="8px">
          <StatChip
            label="R²"
            value={formatPct(modelDiagnostics.rSquared)}
            band={getRSquaredBand(modelDiagnostics.rSquared)}
            tooltip="What percentage of sale price variation is explained by the model's features (GLA, beds, baths, age, etc.). Higher is better — below 50% means the model cannot reliably identify what drives price in this market."
          />
          {modelDiagnostics.crossValidatedStandardErrorRatio !== undefined && (
            <StatChip
              label="Out-of-sample Error"
              value={formatPct(modelDiagnostics.crossValidatedStandardErrorRatio)}
              band={getStdErrorRatioBand(modelDiagnostics.crossValidatedStandardErrorRatio)}
              tooltip="The average sale-price prediction error on transactions held out from five separate model fits. Unlike the standard error ratio, this measures performance on data the model did not train on."
            />
          )}
          <StatChip
            label="Std Error"
            value={
              AloftPrice.fromNumericPrice(modelDiagnostics.standardError)?.toString(true) ?? '—'
            }
            tooltip="The average dollar difference between the model's predicted prices and actual sale prices. Gives a sense of the typical prediction error in dollar terms."
          />
          <StatChip
            label="Std Error Ratio"
            value={formatPct(modelDiagnostics.standardErrorRatio)}
            band={getStdErrorRatioBand(modelDiagnostics.standardErrorRatio)}
            tooltip="The standard error expressed as a percentage of the mean sale price. Above 12% means the model's predictions are too imprecise to reliably support adjustments, even if the unit rates are directionally sound."
          />
          <StatChip
            label="Transactions"
            value={modelDiagnostics.transactionCount.toLocaleString()}
            tooltip="The number of comparable sales used to fit the model. More transactions generally produce more stable and reliable unit rates."
          />
        </Flex>
        {!modelDiagnostics.reliable && (
          <Flex flexDirection="column" gap="6px">
            {modelDiagnostics.rSquared < MODEL_RELIABLE_R_SQUARED_MIN && (
              <Typography variant="body2" color="alertError">
                The model cannot identify what drives price. Less than half the variation in sale
                prices is explained by the features in the model (GLA, beds, baths, age, etc.) — the
                unit rates may be statistically meaningless.
              </Typography>
            )}
            {modelDiagnostics.standardErrorRatio > MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX && (
              <Typography variant="body2" color="alertError">
                The model understands what drives price but cannot predict it precisely. Unit rates
                are directionally sound but carry wide uncertainty — predicted prices are off by
                more than {formatPct(MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX)} on average.
              </Typography>
            )}
            {modelDiagnostics.crossValidatedStandardErrorRatio !== undefined &&
              modelDiagnostics.crossValidatedStandardErrorRatio >
                MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX && (
                <Typography variant="body2" color="alertError">
                  The model does not predict held-out sales precisely enough. Its out-of-sample
                  error exceeds {formatPct(MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX)}.
                </Typography>
              )}
          </Flex>
        )}
      </Flex>

      {/* Unit Rates Table */}
      <Flex flexDirection="column" gap="12px">
        <Typography variant="h4">Unit Rates</Typography>
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Feature</Th>
                <Th>Rate</Th>
                <Th>p-Value</Th>
                <Th>Confidence Interval</Th>
                <Th>Reliable</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Constant row */}
              <Tr>
                <Td>
                  <Typography variant="body2">Constant</Typography>
                </Td>
                <Td>
                  <Typography variant="body2">{formatPrice(constant)}</Typography>
                </Td>
                <Td>
                  <Typography variant="body2" color="textSecondary">
                    —
                  </Typography>
                </Td>
                <Td>
                  <Typography variant="body2" color="textSecondary">
                    —
                  </Typography>
                </Td>
                <Td>
                  <Typography variant="body2" color="textSecondary">
                    —
                  </Typography>
                </Td>
              </Tr>

              {/* Feature rows */}
              {FEATURE_ROWS.map(({ key, label, unit }) => {
                const result = unitRates[key];
                if (!result || !result.rate || !result.pValue || !result.confidenceInterval) {
                  return (
                    <Tr key={key}>
                      <Td>
                        <Typography variant="body2" color="textSecondary">
                          {label}
                        </Typography>
                      </Td>
                      <Td>
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      </Td>
                      <Td>
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      </Td>
                      <Td>
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      </Td>
                      <Td>
                        <DroppedPill />
                      </Td>
                    </Tr>
                  );
                }
                return (
                  <Tr key={key}>
                    <Td>
                      <Typography variant="body2">{label}</Typography>
                    </Td>
                    <Td>
                      <Flex alignItems="baseline" gap="4px">
                        <Typography variant="body2">{formatPrice(result.rate)}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {unit}
                        </Typography>
                        {result.constraint && <ConstraintPill {...result.constraint} />}
                      </Flex>
                    </Td>
                    <Td>
                      <Tooltip
                        label={
                          result.pValue.label === 'Insignificant'
                            ? 'This feature has no statistically detectable relationship with price in this dataset. The unit rate is indistinguishable from zero — there is more than a 5% probability the observed relationship is just random noise in the sample.'
                            : undefined
                        }
                        isDisabled={result.pValue.label !== 'Insignificant'}
                        placement="top"
                      >
                        <Typography variant="body2">
                          {result.pValue.label}{' '}
                          <span style={{ color: '#6b7280' }}>
                            ({result.pValue.value != null ? result.pValue.value.toFixed(3) : '—'})
                          </span>
                        </Typography>
                      </Tooltip>
                    </Td>
                    <Td>
                      <Tooltip
                        label={
                          result.confidenceInterval.label === 'Low'
                            ? 'The confidence interval crosses zero — the true unit rate plausibly has the wrong sign. Even if the point estimate looks reasonable, the model cannot confirm the direction of the relationship, let alone its magnitude.'
                            : undefined
                        }
                        isDisabled={result.confidenceInterval.label !== 'Low'}
                        placement="top"
                      >
                        <Typography variant="body2">
                          {result.confidenceInterval.label}{' '}
                          <span style={{ color: '#6b7280' }}>
                            (Low:{' '}
                            {result.confidenceInterval.low != null
                              ? formatPriceRaw(result.confidenceInterval.low)
                              : '—'}
                            , High:{' '}
                            {result.confidenceInterval.high != null
                              ? formatPriceRaw(result.confidenceInterval.high)
                              : '—'}
                            )
                          </span>
                        </Typography>
                      </Tooltip>
                    </Td>
                    <Td>
                      <ReliablePill reliable={result.reliable} />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
    </Flex>
  );
};
